import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatOrgNumber, formatPostcode } from "@shared/org-number";
import { bandLabel } from "@shared/plan-matrix";
import { PageHeader } from "../../components/PageHeader";
import { foyerApi, type PortalApplication } from "../../http/foyer";
import { tokens } from "../../theme";
import { describeError, useToast } from "../../components/toast";
import { OwnerGate } from "../../components/supplier/OwnerGate";
import { useFormat, type Formatters } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * Organisation applications to the provider's portal (ORG-3,
 * `docs/design/organisation-plans.md`; Sigge 2026-10-06: approval happens in
 * the provider's own pages, separate from Nenda admin).
 *
 * Pending applications come first, each with everything the provider needs
 * to decide: who, org number, address, contact, and the plan chosen at the
 * price it was shown. Approve creates the organisation, mails the contact a
 * link to set a password, and starts the yearly plan. Reject mails a polite
 * no, with an optional note in the provider's own words.
 */

const REFUSAL_CODES = ["not_pending", "no_plan", "name_taken", "email_taken"];

function sek(fmt: Formatters, n: number): string {
  return fmt.currency(n, "SEK", { maximumFractionDigits: n % 1 === 0 ? 0 : 2 });
}

function planText(
  t: Translate,
  fmt: Formatters,
  lang: "sv" | "en",
  a: PortalApplication
): string {
  const k = (name: string, params?: Record<string, unknown>) =>
    translateKey(t, "supplier:supplierApplicationsPage." + name, params);
  if (!a.band || a.yearlyPrice === null) return k("noPlan");
  const films =
    a.films === null ? k("fullLibrary") : k("films", { count: a.films });
  return k("planText", {
    plan: a.planLabel,
    films,
    band: bandLabel(a.band, lang),
    price: sek(fmt, a.yearlyPrice),
  });
}

export default function SupplierApplicationsPage() {
  const toast = useToast();
  const { t, i18n } = useTranslation(["supplier", "common"]);
  const fmt = useFormat();
  const lang = i18n.language === "sv" ? "sv" : "en";
  const plan = (a: PortalApplication) => planText(t, fmt, lang, a);
  const date = (iso: string | null) => (iso ? fmt.date(iso) : "");
  const [apps, setApps] = useState<PortalApplication[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<PortalApplication | null>(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      setApps(await foyerApi.ownerApplications());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(
    a: PortalApplication,
    action: "approve" | "reject",
    rejectNote = ""
  ) {
    setBusy(a.id);
    setError(null);
    try {
      const r =
        action === "approve"
          ? await foyerApi.approveApplication(a.id)
          : await foyerApi.rejectApplication(a.id, rejectNote);
      if (action === "approve" && !r.mailed) {
        // Approved, but the person is not yet able to sign in: needs a human.
        toast.error(
          t("supplierApplicationsPage.approvedNotMailed", {
            company: a.companyName,
            email: a.contactEmail,
          })
        );
      } else {
        toast.success(
          action === "approve"
            ? t("supplierApplicationsPage.approved", {
                company: a.companyName,
                email: a.contactEmail,
              })
            : t("supplierApplicationsPage.rejected", { company: a.companyName })
        );
      }
      await load();
    } catch (e) {
      const code = (e as { code?: string }).code ?? "";
      const why = REFUSAL_CODES.includes(code)
        ? translateKey(t, `supplier:supplierApplicationsPage.refusal.${code}`)
        : e;
      toast.error(
        describeError(
          t,
          action === "approve" ? "approveApplication" : "rejectApplication",
          why,
          { name: a.companyName }
        )
      );
    } finally {
      setBusy(null);
    }
  }

  if (!apps && !error) return <CircularProgress size={24} />;
  const pending = (apps ?? []).filter((a) => a.status === "pending");
  const decided = (apps ?? []).filter((a) => a.status !== "pending");

  return (
    <Stack spacing={3} sx={{ maxWidth: 980 }}>
      <PageHeader
        eyebrow={t("supplierApplicationsPage.eyebrow")}
        title={t("supplierApplicationsPage.title")}
        subtitle={t("supplierApplicationsPage.subtitle")}
      />
      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t("supplierApplicationsPage.waiting", { count: pending.length })}
        </Typography>
        {pending.length === 0 ? (
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {t("supplierApplicationsPage.noneWaiting")}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {pending.map((a) => (
              <Paper
                key={a.id}
                variant="outlined"
                sx={{ p: 2 }}
                data-testid={`application-${a.id}`}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box>
                    <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
                      {a.companyName}
                    </Typography>
                    <Typography variant="body2">
                      {t("supplierApplicationsPage.orgNo", {
                        orgNumber: formatOrgNumber(a.orgNumber),
                      })}
                    </Typography>
                    <Typography variant="body2">
                      {t("supplierApplicationsPage.address", {
                        attention: a.attention,
                        street: a.street,
                        postcode: formatPostcode(a.postcode),
                        city: a.city,
                      })}
                    </Typography>
                    <Typography variant="body2">{a.contactEmail}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, fontWeight: "fontWeightSemibold" }}
                    >
                      {plan(a)}
                    </Typography>
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {t("supplierApplicationsPage.applied", {
                        date: date(a.createdAt),
                      })}
                    </Typography>
                  </Box>
                  <OwnerGate resource="storefront" level="edit">
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Button
                        variant="contained"
                        disabled={busy !== null}
                        onClick={() => void decide(a, "approve")}
                      >
                        {t("supplierApplicationsPage.approve")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        disabled={busy !== null}
                        onClick={() => {
                          setNote("");
                          setRejecting(a);
                        }}
                      >
                        {t("supplierApplicationsPage.reject")}
                      </Button>
                    </Stack>
                  </OwnerGate>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      {decided.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("supplierApplicationsPage.decided")}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("supplierApplicationsPage.columns.organisation")}
                </TableCell>
                <TableCell>
                  {t("supplierApplicationsPage.columns.plan")}
                </TableCell>
                <TableCell>
                  {t("supplierApplicationsPage.columns.decision")}
                </TableCell>
                <TableCell>
                  {t("supplierApplicationsPage.columns.by")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {decided.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    {a.companyName}
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {a.contactEmail}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ typography: "bodySm" }}>{plan(a)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t(
                        a.status === "approved"
                          ? "supplierApplicationsPage.decisionApproved"
                          : "supplierApplicationsPage.decisionRejected",
                        { date: date(a.decidedAt) }
                      )}
                      color={a.status === "approved" ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell sx={{ typography: "bodySm" }}>
                    {a.decidedBy}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("supplierApplicationsPage.rejectTitle", {
            company: rejecting?.companyName,
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t("supplierApplicationsPage.rejectBody", {
              email: rejecting?.contactEmail,
            })}
          </Typography>
          <TextField
            label={t("supplierApplicationsPage.noteLabel")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            helperText={t("supplierApplicationsPage.noteHelp")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy !== null}
            onClick={() => {
              const a = rejecting;
              setRejecting(null);
              if (a) void decide(a, "reject", note);
            }}
          >
            {t("supplierApplicationsPage.reject")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
